import React from "react";
import { PowerSkillQuestion } from "../../typings/powerskill";
import PowerSkillAnswerSummary from "./PowerSkillAnswerSummary";
import { Progress } from "../UI/Progress";
import { getAnswerOptions, getCategoryName } from "../../utils/powerskill";

interface PowerSkillTestQuestionProps {
  questions: PowerSkillQuestion[];
  currentQuestionIndex: number;
  answers: { [key: number]: number };
  loading: boolean;
  showAnswersSummary: boolean;
  onAnswer: (questionId: number, answer: number) => void;
  onNext: () => void;
  onPrev: () => void;
  onSubmit: () => void;
  onToggleSummary: (show: boolean) => void;
}

const PowerSkillTestQuestion: React.FC<PowerSkillTestQuestionProps> = ({
  questions,
  currentQuestionIndex,
  answers,
  loading,
  showAnswersSummary,
  onAnswer,
  onNext,
  onPrev,
  onSubmit,
  onToggleSummary,
}) => {
  const answeredCount = Object.keys(answers).length;
  const totalQuestions = questions.length;
  const currentQuestion = questions[currentQuestionIndex];
  const isCurrentAnswered = currentQuestion
    ? answers.hasOwnProperty(currentQuestion.id)
    : false;
  const allAnswered = answeredCount === totalQuestions;

  return (
    <div className="riasec-container">
      {/* Top Progress Section */}
      <div className="test-header">
        <div className="w-full">
          <div className="flex flex-wrap">
            <div className="w-full px-3">
              <div className="progress-section">
                <div className="flex justify-between items-center mb-2">
                  <span className="question-counter">
                    {currentQuestionIndex + 1} / {totalQuestions}
                  </span>
                  <span className="progress-text">
                    {Math.round(((currentQuestionIndex + 1) / totalQuestions) * 100)}%
                  </span>
                </div>
                <Progress
                  value={((currentQuestionIndex + 1) / totalQuestions) * 100}
                  className="flex-1 h-2 mt-2 bg-muted"
                />
                {currentQuestion && (
                  <div className="mt-2 text-xs font-bold text-orange-600 uppercase tracking-wider">
                    Aspek: {getCategoryName(currentQuestion.type)}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Question Content */}
      <div className="test-content py-8 px-3">
        <div className="w-full p-0">
          <div className="flex flex-wrap justify-center">
            <div className="w-full md:w-8/12 lg:w-6/12">
              <div className="question-card relative">
                {loading && (
                  <div className="question-loading-overlay absolute inset-0 bg-white/50 z-10 flex items-center justify-center">
                    <div
                      className="animate-spin rounded-full border-4 border-gray-300 border-t-orange-600 w-8 h-8"
                      role="status"
                    >
                    </div>
                  </div>
                )}

                <div className="question-text text-xl font-medium mb-8 text-center min-h-[100px] flex items-center justify-center">
                  {currentQuestion?.question}
                </div>

                <div className="answer-buttons flex flex-col gap-3">
                  {getAnswerOptions().map((option) => (
                    <button
                      key={option.score}
                      className={`answer-btn p-4 border rounded-xl font-bold transition-all text-left ${
                        answers[currentQuestion?.id] === option.score
                          ? "bg-orange-600 text-white border-orange-600 shadow-md"
                          : "bg-white text-gray-700 border-gray-200 hover:border-orange-300"
                      }`}
                      onClick={() => onAnswer(currentQuestion.id, option.score)}
                      disabled={loading}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="test-navigation px-3 py-4">
        <div className="w-full">
          <div className="flex flex-wrap">
            <div className="w-full">
              <div className="nav-controls flex justify-between items-center max-w-2xl mx-auto">
                <button
                  className="nav-btn nav-btn-prev flex items-center gap-2 text-gray-500 disabled:opacity-30"
                  onClick={onPrev}
                  disabled={currentQuestionIndex === 0}
                >
                  <div className="nav-icon">←</div>
                  <div className="nav-text hidden sm:block">Sebelumnya</div>
                </button>

                <button
                  className="nav-btn nav-btn-summary flex flex-col items-center"
                  onClick={() => onToggleSummary(true)}
                >
                  <small className="text-xs text-gray-400">Ringkasan</small>
                  <div className="summary-count font-bold text-orange-600">
                    {answeredCount}/{totalQuestions}
                  </div>
                </button>

                {currentQuestionIndex === totalQuestions - 1 ? (
                  <button
                    className="nav-btn nav-btn-submit px-6 py-2 bg-green-600 text-white rounded-lg font-bold disabled:opacity-50"
                    onClick={onSubmit}
                    disabled={!allAnswered || loading}
                  >
                    {loading ? "Mengirim..." : "Selesai"}
                  </button>
                ) : (
                  <button
                    className="nav-btn nav-btn-next flex items-center gap-2 text-orange-600 font-bold disabled:opacity-30"
                    onClick={onNext}
                    disabled={!isCurrentAnswered}
                  >
                    <div className="nav-text hidden sm:block">Selanjutnya</div>
                    <div className="nav-icon">→</div>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showAnswersSummary && (
        <PowerSkillAnswerSummary
          questions={questions}
          answers={answers}
          onClose={() => onToggleSummary(false)}
          show={showAnswersSummary}
        />
      )}
    </div>
  );
};

export default PowerSkillTestQuestion;
